import { Box, Paper, Typography } from "@mui/material";
import { useColorMode } from "~/providers/color-mode";
import { motion } from "motion/react";
import {
  ANIMATION_DELAYS,
  ANIMATION_DURATIONS,
} from "~/pages/integrations/security-pipeline/animation";
import AnimatedNumber from "~/components/AnimatedNumber";

interface SecurityPipelineGithubPageTabStatsProps {
  animationKey: number;
  total: number;
  totalTarget: string;
  timesFaster?: number;
  showParticles?: boolean;
  onReplay?: () => void;
}

export const SecurityPipelineGithubPageTabStats = ({
  total,
  totalTarget,
  animationKey,
  timesFaster,
  showParticles = false,
  onReplay,
}: SecurityPipelineGithubPageTabStatsProps) => {
  const { mode } = useColorMode();
  const isLight = mode === "light";

  return (
    <Paper
      key={animationKey}
      component={motion.div}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: ANIMATION_DURATIONS.box,
        delay: ANIMATION_DELAYS.grayBox,
        type: "spring",
        stiffness: 120,
        damping: 20,
        bounce: 0.1,
      }}
      sx={{
        position: "relative",
        py: 2,
        px: 3,
        borderRadius: 1.5,
      }}
    >
      {timesFaster && timesFaster > 0 && (
        <Box
          key={`badge-${animationKey}`}
          component={motion.div}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: showParticles ? 1 : 0,
            scale: showParticles ? 1 : 0.8,
          }}
          transition={{
            duration: 0.4,
            delay: showParticles ? ANIMATION_DELAYS.badge : 0,
            type: "spring",
            stiffness: 200,
          }}
          onClick={onReplay}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          sx={{
            position: "absolute",
            top: "4px",
            right: "4px",
            borderRadius: "4px",
            border: (theme) => `1px solid ${theme.palette.primary.darker}`,
            px: "6px",
            cursor: "pointer",
          }}
        >
          <Typography
            fontWeight={600}
            fontSize={10}
            color={isLight ? "primary.darker" : "primary.light"}
          >
            <AnimatedNumber
              value={timesFaster ?? 0}
              delay={showParticles ? ANIMATION_DELAYS.badge : 0}
              suffix="% Faster"
              animationKey={animationKey}
            />
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignSelf: "stretch",
        }}
      >
        <Typography
          color={isLight ? "text.primary" : "text.secondary"}
          variant="h4"
          fontWeight={700}
        >
          <AnimatedNumber
            value={total}
            delay={ANIMATION_DELAYS.grayCounter}
            duration={ANIMATION_DURATIONS.grayCounterTotal}
            animationKey={1}
          />
        </Typography>
        <Typography
          textTransform="uppercase"
          color="text.secondary"
          variant="caption"
        >
          {totalTarget}
        </Typography>
      </Box>
    </Paper>
  );
};
